import { clearJobCardsRecently, clearJobCardsRunning, updateManyJobCardReport } from '.';
import { AppThunk, RootState } from '..';
import JobCardsApi from '~/api/jobCard';
import { IUpdateEntity } from '../types';
import { IJobCardReport } from '~/types/models/jobCardReport';
import { jobCardReportsSelector } from './selectors';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { IJobCardV2 } from '~/types/models/jobCard';
import TimeReportService, { createTimeReportResponse } from '~/api/timeReportService';
import { TimeReportRequest, TimeReportUpdateResponse, TimeReportUpdateRequest } from '~/types/request/timeReport';
import TimeReportAdapters from '~/helpers/adapters/timeReport';
import { setNotification } from '../main';
import { getEmployeeId, getBranch } from '~/utils/sessionStorage';
import { t } from '~/utils/lang';
import { toggleLoader } from './index';

export const fetchJobCardsRunning = createAsyncThunk<{ list: IJobCardV2[]; totalCount?: number }>(
  'jobCard/fetchJobCardsRunning',
  async (_payload, { getState }) => {
    try {
      const { jobCard, main } = getState() as RootState;

      if (jobCard.jobCardsRunning.pagination.isMax) {
        return { list: [] };
      }

      const jcStatuses = Object.values(main.dictionaries.jcStatuses.entities)
        .filter((status) => status?.showOnTimePortal)
        .map((status) => status?.id);

      const filter = {
        RunningJobCards: getEmployeeId(),
        Branch: getBranch(),
        'JobStatus.ID': jcStatuses,
        ...jobCard?.filter,
      };

      const { list, totalCount } = await JobCardsApi.loadJobCards(filter, jobCard.jobCardsRunning.pagination);

      list.forEach((jobCard) => {
        const { Reports = [] } = jobCard;
        jobCard.Reports = Reports.map((report) => TimeReportAdapters.adaptReportFromServerToClient(report));
      });

      return { list, totalCount };
    } catch (error) {
      console.error(error);
      return { list: [] };
    }
  },
);

export const fetchJobCardsNotRunning = createAsyncThunk<{ list: IJobCardV2[]; totalCount?: number }>(
  'jobCard/fetchJobCardsNotRunning',
  async (_payload, { getState }) => {
    try {
      const { jobCard, main } = getState() as RootState;

      if (jobCard.jobCardsNotRunning.pagination.isMax) {
        return { list: [] };
      }

      const jcStatuses = Object.values(main.dictionaries.jcStatuses.entities)
        .filter((status) => status?.showOnTimePortal)
        .map((status) => status?.id);

      const filter = {
        Branch: getBranch(),
        'JobStatus.ID': jcStatuses,
        ...jobCard?.filter,
      };

      const { list, totalCount } = await JobCardsApi.loadJobCards(filter, jobCard.jobCardsNotRunning.pagination);

      list.forEach((jobCard) => {
        const { Reports = [] } = jobCard;
        jobCard.Reports = Reports.map((report) => TimeReportAdapters.adaptReportFromServerToClient(report));
      });

      return { list, totalCount };
    } catch (error) {
      console.error(error);
      return { list: [] };
    }
  },
);

export const fetchJobCardsRecently = createAsyncThunk<{ list: IJobCardV2[]; totalCount?: number }>(
  'jobCard/fetchJobCardsRecently',
  async (_payload, { getState }) => {
    try {
      const { jobCard, main } = getState() as RootState;

      if (jobCard.jobCardsRecently.pagination.isMax) {
        return { list: [] };
      }

      const jcStatuses = Object.values(main.dictionaries.jcStatuses.entities)
        .filter((status) => status?.showOnTimePortal)
        .map((status) => status?.id);

      const filter = {
        RecentlyJobCards: getEmployeeId(),
        Branch: getBranch(),
        'JobStatus.ID': jcStatuses,
        ...jobCard?.filter,
      };

      const { list, totalCount } = await JobCardsApi.loadJobCards(filter, jobCard.jobCardsRecently.pagination);

      list.forEach((jobCard) => {
        const { Reports = [] } = jobCard;
        jobCard.Reports = Reports.map((report) => TimeReportAdapters.adaptReportFromServerToClient(report));
      });

      return { list, totalCount };
    } catch (error) {
      console.error(error);
      return { list: [] };
    }
  },
);

export const updateStatusOfJobCardReportsAction =
  async (payload: IUpdateEntity<IJobCardReport>): Promise<AppThunk> =>
  async (dispatch, getState) => {
    const reports = jobCardReportsSelector.selectAll(getState());

    const { id, data } = await JobCardsApi.updateJobCardReport(payload.id, payload.changes);

    let newReports = reports.map(({ ReportLineId }) => ({ id: ReportLineId, changes: { isRun: false } }));

    newReports = [...newReports, { id, changes: { isRun: Boolean(data.isRun) } }];

    dispatch(updateManyJobCardReport(newReports));
  };

export const createTimeReport = createAsyncThunk<createTimeReportResponse | undefined, TimeReportRequest>(
  'jobCard/createTimeReport',
  async (payload, { dispatch }) => {
    dispatch(toggleLoader({ key: 'isLoadingCreateReport', value: true }));
    try {
      const { isStartReport = false, ...data } = payload;

      const adaptPayload = TimeReportAdapters.adaptReportFromClientToServer(data);

      let reports;

      if (isStartReport) {
        reports = await TimeReportService.startReport(adaptPayload);
      } else {
        reports = await TimeReportService.create(adaptPayload);
      }

      if (reports.result) {
        return {
          result: TimeReportAdapters.adaptReportFromServerToClient(reports.result),
          stoppedIds: reports.stoppedIds,
          stopedTimeReports: reports.stopedTimeReports,
        };
      } else {
        throw new Error('Report generation failed');
      }
    } catch (error: any) {
      // FIXME: if add more case, rewrite it
      let msg = t.t('cards_page.reports_tab.update_not_allowed', { ids: error.message || error });
      if (error.status === 403) {
        msg = error.message;
      }
      dispatch(setNotification({ message: msg, type: 'danger' }));
      throw new Error(error?.message || error);
    } finally {
      dispatch(toggleLoader({ key: 'isLoadingCreateReport', value: false }));
    }
  },
);

export const updateTimeReport = createAsyncThunk<TimeReportUpdateResponse, TimeReportUpdateRequest>(
  'jobCard/updateTimeReport',
  async (timeReportUpdate, { dispatch }) => {
    try {
      const { payload, jobCardId } = timeReportUpdate;

      const adaptPayload = TimeReportAdapters.adaptReportFromClientToServer(payload);

      let response = await TimeReportService.update(adaptPayload);

      let result = { payload: { id: '', changes: {} }, jobCardId: '' };

      if (response.result) {
        response.result = TimeReportAdapters.adaptReportFromServerToClient(response.result);

        const data = { id: response.result._id, changes: response.result };

        result = { payload: data, jobCardId };
      }

      return result;
    } catch (error: any) {
      const msg = t.t('cards_page.reports_tab.update_not_allowed', { ids: error.message || error });
      dispatch(setNotification({ message: msg, type: 'danger' }));
      throw new Error(error?.message || error);
    }
  },
);
